import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BadgeCheck, Check, ExternalLink, Info, Lock, Layers, LifeBuoy, Building2 } from 'lucide-react';
import { useLang } from '../components/LanguageProvider';
import { NwBaglanti, NwLogo } from '../components/NorthwestOrtaklik';
import translations from '../translations';
import { NW_LLC_BAGLANTISI, NW_TEMSILCI_BAGLANTISI, NW_REHBER_BANNER } from '../lib/northwest';

/**
 * ABD'DE LLC KURULUMU — Northwest Registered Agent rehberi (/us-llc-formation).
 *
 * Neden var: WhowSell, danışmanlık verdiği satıcıları ABD'de şirket kurarken
 * Northwest'e yönlendiriyor (Awin, yayıncı 2582277). Awin'e kayıtlı tanıtım
 * alanında Northwest'i anlatan bir içerik olmadığı için üyelik askıya alma
 * sürecine girdi (19 Ekim 2026). Bu sayfa o içerik: süreci anlatır, Northwest'i
 * programın kendi içerik şablonuna yakın bir dille tanıtır, resmi bağlantı ve
 * banner'ı kullanır. Ana sayfadaki bölümden ve altbilgiden bağlanır.
 *
 * Programın şartları (Northwest Terms) ve bu sayfadaki karşılıkları:
 *  - "Must be relevant website content" → pazaryeri satıcısının ABD şirketi.
 *  - Affiliate açıklaması bağlantıların olduğu sayfada (FTC) → "Açıklama" kutusu.
 *  - "A Northwest Registered Agent affiliate" ifadesi → açıklama kutusunda.
 *  - Rakip marka / logo YOK; tıklayana ödül / indirim vaadi YOK.
 *  - Fiyat yalnız Northwest'in kendi şablonundaki haliyle ve "değişebilir" notuyla
 *    (karşılaştırma yapılmıyor).
 *  - Hukuki / vergi tavsiyesi verilmez; Northwest'in bağımsız olduğu ve
 *    siparişi onun yürüttüğü açıkça yazar ("customer confusion" maddesi).
 *
 * Yerleşim AboutPage ile aynı aileden (rozet · gradyanlı başlık · glow-card).
 * `SectionReveal` bilerek yok: içerik sayfa açılır açılmaz okunur olsun.
 */

const NEDEN_IKONLARI = [Layers, Lock, Building2, BadgeCheck, LifeBuoy];

/**
 * Resmi 300x250 banner (Awin #4428985). Görsel engellenirse (reklam
 * engelleyici) boş çerçeve kalmasın diye kutu tamamen çekilir; sayfanın
 * metin bağlantıları zaten aynı yere gidiyor.
 */
function RehberBanner({ etiket, ariaYeniSekme }) {
  const [yok, setYok] = useState(false);
  if (yok) return null;
  const b = NW_REHBER_BANNER;
  return (
    <figure className="mx-auto w-full" style={{ maxWidth: `${b.genislik}px` }}>
      <figcaption className="text-[11px] mb-2 text-center dark:text-white/40 text-slate-500">{etiket}</figcaption>
      <a href={b.href} target="_blank" rel="sponsored noopener" className="block rounded-xl overflow-hidden">
        <img
          src={b.src}
          width={b.genislik}
          height={b.yukseklik}
          alt="Northwest Registered Agent"
          loading="lazy"
          decoding="async"
          onError={() => setYok(true)}
          className="block w-full h-auto"
          draggable="false"
        />
        <span className="sr-only"> ({ariaYeniSekme})</span>
      </a>
    </figure>
  );
}

export default function AbdLlcPage() {
  const { lang } = useLang();
  const t = translations[lang].usLlc;
  const p = translations[lang].partner;
  const ortak = translations[lang].common;

  return (
    <div className="min-h-screen dark:bg-slate-950 bg-[#f0f4f8] dark:text-white text-slate-900 pt-20">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl" />
      </div>

      <div data-testid="us-llc-page" className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        <div className="mb-8 sm:mb-10">
          <Link
            to="/"
            className="wh-nw-geri inline-flex items-center gap-2 text-sm dark:text-white/50 text-slate-500 hover:dark:text-white hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            {ortak.home}
          </Link>
        </div>

        {/* BAŞLIK */}
        <header className="max-w-3xl mb-10 sm:mb-14">
          <span className="inline-block mb-4 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase border dark:border-cyan-500/30 border-cyan-500/40 dark:bg-cyan-500/10 bg-cyan-50 dark:text-cyan-300 text-cyan-700">
            {t.badge}
          </span>
          <h1 className="text-[28px] leading-tight sm:text-4xl lg:text-5xl font-bold mb-5 break-words">
            <span className="dark:text-white text-slate-900">{t.titlePre}</span>
            <span className="animated-gradient-text">{t.titleAccent}</span>
          </h1>
          <p className="text-sm sm:text-base lg:text-lg leading-relaxed dark:text-white/60 text-slate-600 break-words">{t.intro}</p>
          <div className="mt-6">
            <NwLogo alt={p.logoAlt} ariaYeniSekme={p.newTab} />
          </div>
        </header>

        {/* ADIMLAR — numaralı liste: sıra gerçekten önemli (eyalet → kuruluş
            → temsilci → EIN → satış). */}
        <section className="mb-12 sm:mb-16" aria-labelledby="nw-adimlar">
          <h2 id="nw-adimlar" className="text-lg sm:text-xl lg:text-2xl font-bold mb-5 sm:mb-6 dark:text-white text-slate-900">{t.stepsTitle}</h2>
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {t.steps.map((adim, i) => (
              <li key={adim.title} className="glow-card rounded-2xl p-5 sm:p-7 h-full min-w-0" style={{ '--glow-color': 'rgba(6, 182, 212, 0.25)' }}>
                <div className="flex items-center gap-3 mb-3 min-w-0">
                  <span className="inline-flex w-9 h-9 shrink-0 rounded-xl items-center justify-center font-mono text-sm font-bold dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-500/25 dark:text-cyan-300 text-cyan-700">
                    {i + 1}
                  </span>
                  <h3 className="text-[15px] sm:text-base font-bold dark:text-white text-slate-900 break-words min-w-0">{adim.title}</h3>
                </div>
                <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 break-words">{adim.text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* İKİ HİZMET — Northwest'in iş ortaklarına önerdiği kart biçimi
            (içerik şablonu "Most Popular Services"). */}
        <section className="mb-6" aria-labelledby="nw-hizmetler">
          <h2 id="nw-hizmetler" className="text-lg sm:text-xl lg:text-2xl font-bold mb-5 sm:mb-6 dark:text-white text-slate-900">{t.servicesTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {t.services.map((h) => (
              <div key={h.kind} className="glow-card rounded-2xl p-5 sm:p-7 h-full flex flex-col min-w-0" style={{ '--glow-color': 'rgba(139, 92, 246, 0.25)' }}>
                <h3 className="text-[15px] sm:text-base font-bold dark:text-white text-slate-900 break-words">{h.title}</h3>
                <p className="mt-1 mb-4 text-xl sm:text-2xl font-bold dark:text-cyan-300 text-cyan-700 break-words">{h.price}</p>
                <ul className="space-y-2.5 mb-6">
                  {h.items.map((m) => (
                    <li key={m} className="flex items-start gap-2.5 min-w-0">
                      <Check className="w-4 h-4 mt-0.5 shrink-0 dark:text-emerald-400 text-emerald-600" aria-hidden="true" />
                      <span className="text-[13px] sm:text-sm dark:text-white/70 text-slate-700 break-words">{m}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <NwBaglanti
                    href={h.kind === 'agent' ? NW_TEMSILCI_BAGLANTISI : NW_LLC_BAGLANTISI}
                    ariaYeniSekme={p.newTab}
                    className="wh-cta wh-nw-cta w-full group bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_10px_30px_rgba(6,182,212,0.2)] active:scale-[0.97]"
                  >
                    {h.cta}
                    <ExternalLink aria-hidden="true" />
                  </NwBaglanti>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs dark:text-white/40 text-slate-500 break-words">{t.priceNote}</p>
        </section>

        {/* NEDEN NORTHWEST — programın "Core Messaging" başlıkları. */}
        <section className="mt-12 sm:mt-16 mb-12 sm:mb-16" aria-labelledby="nw-neden">
          <h2 id="nw-neden" className="text-lg sm:text-xl lg:text-2xl font-bold mb-5 sm:mb-6 dark:text-white text-slate-900">{t.whyTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {t.why.map((n, i) => {
              const Ikon = NEDEN_IKONLARI[i] || BadgeCheck;
              return (
                <div key={n.title} className="glow-card rounded-2xl p-5 sm:p-6 h-full min-w-0" style={{ '--glow-color': 'rgba(6, 182, 212, 0.2)' }}>
                  <span className="inline-flex w-10 h-10 rounded-xl items-center justify-center mb-3 dark:bg-cyan-500/10 bg-cyan-50 border dark:border-cyan-500/20 border-cyan-500/25 dark:text-cyan-300 text-cyan-600">
                    <Ikon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <h3 className="text-[15px] font-bold mb-1.5 dark:text-white text-slate-900 break-words">{n.title}</h3>
                  <p className="text-[13px] leading-relaxed dark:text-white/60 text-slate-600 break-words">{n.text}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* RESMİ BANNER */}
        <div className="mb-12 sm:mb-16">
          <RehberBanner etiket={t.bannerLabel} ariaYeniSekme={p.newTab} />
        </div>

        {/* AÇIKLAMA — FTC açıklaması + programın istediği ifade + yasal not. */}
        <section className="glow-card rounded-2xl p-5 sm:p-7 mb-12 sm:mb-16 border-l-2 dark:border-l-amber-400/60 border-l-amber-500/70" style={{ '--glow-color': 'rgba(245, 158, 11, 0.2)' }} aria-labelledby="nw-aciklama">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 shrink-0 dark:text-amber-300 text-amber-700" aria-hidden="true" />
            <h2 id="nw-aciklama" className="text-[15px] sm:text-base font-bold dark:text-white text-slate-900">{t.disclosureTitle}</h2>
          </div>
          <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/65 text-slate-700 mb-3 break-words">{t.disclosure}</p>
          <p className="text-[13px] sm:text-sm leading-relaxed font-semibold dark:text-white/80 text-slate-800 mb-3 break-words">{t.affiliate}</p>
          <p className="text-xs leading-relaxed dark:text-white/45 text-slate-500 break-words">{t.legal}</p>
        </section>

        {/* İLETİŞİM ÇAĞRISI — danışmanlık tarafımız. */}
        <section className="glow-card rounded-2xl p-6 sm:p-8 lg:p-10 text-center" style={{ '--glow-color': 'rgba(6, 182, 212, 0.3)' }}>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 dark:text-white text-slate-900">{t.ctaTitle}</h2>
          <p className="text-[13px] sm:text-sm leading-relaxed dark:text-white/60 text-slate-600 max-w-xl mx-auto mb-6 break-words">{t.ctaText}</p>
          <Link
            to="/contact"
            className="wh-cta wh-nw-cta group bg-gradient-to-b from-cyan-400 to-cyan-500 hover:from-cyan-300 hover:to-cyan-400 text-slate-950 transition-all shadow-[0_0_0_1px_rgba(6,182,212,0.35),0_10px_30px_rgba(6,182,212,0.25)] active:scale-[0.97]"
          >
            {t.ctaButton}
            <ArrowRight className="group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </Link>
        </section>
      </div>
    </div>
  );
}
